/**
 * Embed скрипт для интеграции виджета на сайт
 * Entry point для UMD bundle
 */

import { createRoot } from 'react-dom/client';
import App from './App';
import { LarteChatConfig, SupportedLanguage } from './types/config.types';
import { useUIStore } from './store/uiStore';
import { useConfigStore } from './store/configStore';
import { useChatStore } from './store/chatStore';
import { clearAllStorage } from './utils/storage';
import { chatLogger } from './utils/chatLogger';

/**
 * Глобальный интерфейс Window
 */
declare global {
  interface Window {
    LarteChat?: LarteChatConfig;
    LarteChatWidget?: {
      init: (config?: Partial<LarteChatConfig>) => void;
      setConfig: (config: Partial<LarteChatConfig>) => void;
      open: () => void;
      close: () => void;
      toggle: () => void;
      setLanguage: (lang: string) => void;
      startNewConversation: () => void;
      clearStorage: () => void;
      getState: () => { conversationId: string | null; userId: string; messagesCount: number };
      destroy: () => void;
    };
  }
}

/**
 * Класс виджета
 */
class LarteChatWidget {
  private root: ReturnType<typeof createRoot> | null = null;
  private container: HTMLDivElement | null = null;

  /**
   * Инициализация виджета
   */
  init() {
    chatLogger.log('%c🎨 Larte Chat Widget', 'font-size: 16px; color: #968b74; font-weight: bold');
    chatLogger.log('Версия: 1.0.0');

    // Проверяем наличие конфигурации
    if (!window.LarteChat) {
      chatLogger.alwaysError('[Larte Chat] Конфигурация не найдена. Добавьте window.LarteChat перед загрузкой скрипта.');
      return;
    }

    // Если уже инициализирован — не дублируем
    if (this.root || document.getElementById('larte-chat-widget-root')) {
      return;
    }

    // Создаем контейнер для виджета
    this.container = document.createElement('div');
    this.container.id = 'larte-chat-widget-container';
    document.body.appendChild(this.container);

    // Инжектим CSS
    this.injectStyles();

    // Создаем React root и рендерим App
    this.root = createRoot(this.container);
    this.root.render(<App />);

    chatLogger.log('✅ Виджет инициализирован');
  }

  /**
   * Инжектит глобальные стили виджета
   */
  private injectStyles() {
    // Стили уже включены в компоненты через CSS Modules
    // Но добавим базовые глобальные стили если нужно
    const style = document.createElement('style');
    style.id = 'larte-chat-global-styles';
    style.textContent = `
      #larte-chat-widget-container {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Удаляет виджет со страницы (но оставляет window.LarteChatWidget API)
   */
  destroy() {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }

    const container =
      this.container || (document.getElementById('larte-chat-widget-container') as HTMLDivElement | null);
    if (container && container.parentNode) {
      container.parentNode.removeChild(container);
    }
    this.container = null;

    const styles = document.getElementById('larte-chat-global-styles');
    if (styles && styles.parentNode) {
      styles.parentNode.removeChild(styles);
    }

    chatLogger.log('✅ Виджет удален');
  }
}

/**
 * Автоматическая инициализация при загрузке
 */
function autoInit() {
  // Создаем singleton, чтобы можно было переинициализировать без перезагрузки скрипта
  const widget = new LarteChatWidget();

  // Экспортируем API методы
  window.LarteChatWidget = {
    init: (config?: Partial<LarteChatConfig>) => {
      if (config) {
        window.LarteChat = { ...(window.LarteChat || ({} as LarteChatConfig)), ...config } as LarteChatConfig;
      }
      widget.init();
    },

    setConfig: (config: Partial<LarteChatConfig>) => {
      window.LarteChat = { ...(window.LarteChat || ({} as LarteChatConfig)), ...config } as LarteChatConfig;
    },

    open: () => {
      // Lazy: если ещё не инициализирован — пробуем инициализировать
      if (!document.getElementById('larte-chat-widget-root')) {
        widget.init();
      }
      useUIStore.getState().open();
    },

    close: () => {
      useUIStore.getState().close();
    },

    toggle: () => {
      if (!document.getElementById('larte-chat-widget-root')) {
        widget.init();
      }
      useUIStore.getState().toggle();
    },

    setLanguage: (lang: string) => {
      const allowed: SupportedLanguage[] = ['ru', 'en', 'de', 'fr'];
      if (allowed.includes(lang as SupportedLanguage)) {
        useConfigStore.getState().setLanguage(lang as SupportedLanguage);
      }
    },

    startNewConversation: () => {
      useChatStore.getState().startNewConversation();
    },

    clearStorage: () => {
      useChatStore.getState().startNewConversation();
      clearAllStorage();
    },

    getState: () => {
      const { conversationId, userId, messages } = useChatStore.getState();
      return { conversationId, userId, messagesCount: messages.length };
    },

    destroy: () => {
      widget.destroy();
    },
  };

  // Авто-init (только если конфиг задан заранее)
  if (window.LarteChat) {
    widget.init();
  }
}

// Запускаем после загрузки DOM
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', autoInit);
} else {
  autoInit();
}

export {};
